import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, InputNumber, Modal, Skeleton, Space, Typography } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';

export interface DataRetentionConfig {
  chatMessagesDays: number;
  notificationsDays: number;
  clientIncidentsDays: number;
  liveLogsDays: number;
}

interface AdminDataRetentionView extends DataRetentionConfig {
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Same limit the API enforces (shared-types DATA_RETENTION_MAX_DAYS). */
export const RETENTION_MAX_DAYS = 3650;

const FIELDS: { key: keyof DataRetentionConfig; label: string; hint: string }[] = [
  {
    key: 'chatMessagesDays',
    label: 'Chat messages',
    hint: 'Never removed before the message’s own 30-day expiry.',
  },
  { key: 'notificationsDays', label: 'Notifications', hint: 'In-app notification center rows.' },
  {
    key: 'clientIncidentsDays',
    label: 'App incidents',
    hint: 'Only acknowledged / resolved / ignored incidents not seen since. Open incidents are always kept.',
  },
  { key: 'liveLogsDays', label: 'Live & party logs', hint: 'Live stream comments and party room logs.' },
];

const pick = (v: AdminDataRetentionView): DataRetentionConfig => ({
  chatMessagesDays: v.chatMessagesDays,
  notificationsDays: v.notificationsDays,
  clientIncidentsDays: v.clientIncidentsDays,
  liveLogsDays: v.liveLogsDays,
});

/** Windows that now delete MORE than before: newly turned on, or shortened. */
export function shortenedFields(before: DataRetentionConfig, next: DataRetentionConfig): string[] {
  return FIELDS.filter(({ key }) => next[key] > 0 && (before[key] === 0 || next[key] < before[key])).map(
    (f) => f.label,
  );
}

function serverError(e: unknown): string {
  const data = (e as { response?: { data?: { message?: string } } }).response?.data;
  return data?.message ?? 'Could not save data retention';
}

/**
 * Settings → App configuration → Data retention. Days to keep each fast-growing,
 * non-financial table; 0 = keep forever (the default). A daily job deletes older
 * rows. Money and audit records are never part of this.
 */
export function DataRetentionCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('data_retention.edit');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'data-retention'],
    queryFn: async () => (await api.get<AdminDataRetentionView>('/admin/app-config/data-retention')).data,
  });
  const [draft, setDraft] = useState<DataRetentionConfig | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(pick(data));
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async (body: DataRetentionConfig) =>
      (await api.patch<AdminDataRetentionView>('/admin/app-config/data-retention', body)).data,
    onSuccess: (saved) => {
      message.success('Data retention saved');
      qc.setQueryData(['admin', 'data-retention'], saved);
      setDraft(pick(saved));
    },
    onError: (e) => message.error(serverError(e)),
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load data retention"
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  if (!draft) return null;

  const valid = FIELDS.every(
    ({ key }) => Number.isInteger(draft[key]) && draft[key] >= 0 && draft[key] <= RETENTION_MAX_DAYS,
  );
  const dirty = JSON.stringify(draft) !== JSON.stringify(pick(data));

  const onSave = () => {
    const more = shortenedFields(pick(data), draft);
    if (more.length === 0) {
      save.mutate(draft);
      return;
    }
    Modal.confirm({
      title: 'Delete older data?',
      content: `${more.join(', ')}: rows older than the new window are permanently deleted at the next daily run. This cannot be undone.`,
      okText: 'Save and delete',
      okButtonProps: { danger: true },
      onOk: () => save.mutate(draft),
    });
  };

  return (
    <Card
      title={
        <Space>
          <DeleteOutlined />
          Data retention
        </Space>
      }
      extra={
        data.updatedAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last changed {new Date(data.updatedAt).toLocaleString('en-IN')}
          </Typography.Text>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 560 }}>
        <Typography.Text type="secondary">
          Days to keep each kind of data. 0 = keep forever. Older rows are deleted once a day. Wallet,
          payment and audit records are never deleted here.
        </Typography.Text>
        {FIELDS.map(({ key, label, hint }) => (
          <div key={key}>
            <Typography.Text>{label} (days)</Typography.Text>
            <div>
              <InputNumber
                aria-label={`${label} (days)`}
                min={0}
                max={RETENTION_MAX_DAYS}
                precision={0}
                value={draft[key]}
                disabled={!canEdit}
                onChange={(v) => setDraft({ ...draft, [key]: v ?? 0 })}
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {draft[key] === 0 ? 'Kept forever. ' : ''}
              {hint}
            </Typography.Text>
          </div>
        ))}
        {canEdit && (
          <Space>
            <Button
              type="primary"
              data-testid="data-retention-save"
              disabled={!dirty || !valid}
              loading={save.isPending}
              onClick={onSave}
            >
              Save
            </Button>
            {dirty && (
              <Button onClick={() => setDraft(pick(data))} disabled={save.isPending}>
                Discard
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
}
